import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { mainServices, reviews, faqs, companyInfo } from '../data/content';

export default function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center">
        <div className="absolute inset-0 bg-slate-900/70 z-10" />
        <div 
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1585704032915-c3400ca199e7?auto=format&fit=crop&q=80")' }}
        />
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Fast, Reliable Plumbing Services Across Montreal.
            </h1>
            <p className="text-lg md:text-xl mb-8 text-slate-200">
              From emergency repairs to complete plumbing installations, our licensed plumbers help homes and businesses stay running smoothly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button href={`tel:${companyInfo.phone}`} variant="accent" className="text-lg">
                <SafeIcon name="Phone" className="mr-2" /> Call Now
              </Button>
              <Button to="/request-service" variant="primary" className="text-lg !bg-white !text-primary hover:!bg-slate-100">
                Request Service
              </Button>
            </div>
            
            <div className="mt-8 flex flex-wrap gap-6 text-sm font-medium">
              <div className="flex items-center gap-2"><SafeIcon name="CheckCircle" className="text-accent" /> 24/7 Emergency Service</div>
              <div className="flex items-center gap-2"><SafeIcon name="CheckCircle" className="text-accent" /> Licensed Plumbers</div>
              <div className="flex items-center gap-2"><SafeIcon name="CheckCircle" className="text-accent" /> Upfront Pricing</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-primary text-white py-8 -mt-2 relative z-30 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
           <div className="flex flex-col items-center"><SafeIcon name="Clock" className="text-3xl mb-2 text-secondary" /> <span className="font-semibold">Fast Response</span></div>
           <div className="flex flex-col items-center"><SafeIcon name="Shield" className="text-3xl mb-2 text-secondary" /> <span className="font-semibold">Licensed & Insured</span></div>
           <div className="flex flex-col items-center"><SafeIcon name="DollarSign" className="text-3xl mb-2 text-secondary" /> <span className="font-semibold">Honest Pricing</span></div>
           <div className="flex flex-col items-center"><SafeIcon name="ThumbsUp" className="text-3xl mb-2 text-secondary" /> <span className="font-semibold">Satisfaction Guaranteed</span></div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-text mb-4">Professional Plumbing Solutions</h2>
            <p className="text-muted max-w-2xl mx-auto">We handle everything from minor household leaks to major commercial plumbing emergencies.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainServices.map((service, idx) => (
              <motion.div 
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 text-primary">
                  <SafeIcon name={service.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-muted mb-4">{service.desc}</p>
                <Link to={`/services#${service.id}`} className="text-secondary font-semibold hover:text-primary flex items-center gap-1 text-sm">
                  Learn more <SafeIcon name="ArrowRight" />
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button to="/services" variant="outline">View All Services</Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2">
            <img 
              src="https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&q=80" 
              alt="Professional Plumber" 
              className="rounded-2xl shadow-xl"
            />
          </div>
          <div className="lg:w-1/2">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Montreal's Most Trusted Plumbing Team</h2>
            <p className="text-muted mb-8 text-lg">For over 15 years, BlueFlow Plumbing & Drain has been delivering top-tier plumbing services with a commitment to honesty, quality, and complete customer satisfaction.</p>
            
            <ul className="space-y-4">
              {[
                "Locally owned and operated in Montreal",
                "Fully licensed (RBQ) and insured professionals",
                "Clean, respectful technicians who protect your home",
                "State-of-the-art diagnostic equipment"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <SafeIcon name="CheckCircle" className="text-secondary text-xl mt-1 shrink-0" />
                  <span className="text-lg font-medium text-text">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
               <Button to="/about" variant="primary">Learn More About Us</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Clients Say</h2>
            <div className="flex justify-center gap-1 text-yellow-400 text-2xl mb-2">
              {[1,2,3,4,5].map(i => <SafeIcon key={i} name="Star" fill="currentColor" />)}
            </div>
            <p className="text-muted">Over 500+ 5-Star Reviews on Google</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review) => (
              <div key={review.id} className="bg-white p-8 rounded-xl shadow-sm border border-slate-100">
                <div className="flex gap-1 text-yellow-400 mb-4">
                  {[1,2,3,4,5].map(i => <SafeIcon key={i} name="Star" fill="currentColor" className="text-sm" />)}
                </div>
                <p className="text-text italic mb-6">"{review.text}"</p>
                <div>
                  <p className="font-bold">{review.name}</p>
                  <p className="text-sm text-muted">{review.area}, QC</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}